import React from 'react';
import { ChevronLeft } from 'lucide-react';
import MemberAvatar from './MemberAvatar';
import ChatHeaderMenu from './ChatHeaderMenu';
import type { ArtifactKind } from '../../services/artifactService';
import type { ChatSession, MemberSpec } from './types';
import { TYPE } from '../../constants/typeScale';

/**
 * The chat thread's top bar: a mobile back button, the group identity (a generated logo or the stacked
 * member avatars, which open Manage), the title + a status line (export progress / error / member count),
 * and the overflow menu (export / transcript / invite / manage / decisions). Purely presentational —
 * state (exporting/error) and every action come in as props; the parent owns the export + transcript logic.
 */
interface ChatThreadHeaderProps {
  session: ChatSession;
  members: MemberSpec[];
  catalogAvatars: Record<string, string>;
  exporting: ArtifactKind | null;
  exportErr: string;
  onBack?: () => void;
  /**
   * Keep the back control on DESKTOP too. In the workstation's right pane the chat is level 2 of a
   * two-level surface (contacts → chat), so back is the only way home — it is no longer mobile-only.
   */
  backOnDesktop?: boolean;
  onManage?: () => void;
  onInvite?: () => void;
  onRequests?: () => void;
  onExport: (kind: ArtifactKind, format?: 'html') => void;
  onExportTranscript: (format: 'txt') => void;
  onShowDecisions: () => void;
}

export const ChatThreadHeader: React.FC<ChatThreadHeaderProps> = ({
  session,
  members,
  catalogAvatars,
  exporting,
  exportErr,
  onBack,
  backOnDesktop,
  onManage,
  onInvite,
  onRequests,
  onExport,
  onExportTranscript,
  onShowDecisions,
}) => (
  <div className="flex items-center gap-3 px-4 md:px-6 h-16 border-b border-secondary-200 dark:border-secondary-600 shrink-0">
    {onBack && (
      <button
        onClick={onBack}
        aria-label="Back to chats" title="Back to chats"
        className={`${backOnDesktop ? '' : 'md:hidden'} -ml-1 p-1.5 rounded-lg text-secondary-500 hover:text-secondary-800 dark:hover:text-secondary-200 hover:bg-secondary-100 dark:hover:bg-secondary-800 transition-colors`}
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
    )}
    <button
      onClick={onManage}
      disabled={!onManage}
      aria-label="Manage group" title="Manage group"
      className="flex items-center disabled:cursor-default enabled:hover:opacity-80 transition-opacity"
    >
      {session.logoUrl ? (
        // A generated/uploaded chat logo becomes the group's icon (replaces the agent stack).
        <img src={session.logoUrl} alt="" className="w-8 h-8 rounded-full object-cover border-2 border-white dark:border-black" />
      ) : (
        <span className="flex -space-x-2">
          {members.map((m) => (
            <MemberAvatar
              key={m.id}
              avatarIcon={m.avatar_icon}
              avatarPath={m.avatar_path}
              avatarDriveFileId={m.avatar_drive_file_id}
              avatarImage={m.profile_id ? catalogAvatars[m.profile_id] : null}
              conversationId={session.id}
              name={m.name}
              className="w-8 h-8 border-2 border-white dark:border-black"
            />
          ))}
        </span>
      )}
    </button>
    <div className="min-w-0 flex-1">
      <p className={`${TYPE.sectionTitle} text-secondary-900 dark:text-white truncate`}>{session.title}</p>
      <p className={`${TYPE.meta} text-secondary-400`}>
        {exporting ? `Generating ${exporting}…` : exportErr ? <span className="text-secondary-500">{exportErr}</span> : `${members.length + 1} in this chat`}
      </p>
    </div>
    <ChatHeaderMenu
      onExport={onExport}
      onExportTranscript={onExportTranscript}
      onInvite={onInvite}
      onManage={onManage}
      onRequests={onRequests}
      onDecisions={onShowDecisions}
      busy={!!exporting}
    />
  </div>
);

export default ChatThreadHeader;
